"use client";

import React, { useMemo } from "react";
import { DetailModalContent } from "./DetailModalContent";
import { EntityContextProvider } from "../../UniversalDetailPage/contexts/EntityContextProvider";
import type { BaseEntity, SimpleTableConfig } from "../../UniversalCRMTable/types/shared";
import type { DetailActionContext } from "../../UniversalDetailPage/types/detailPage";
import type { LinkedCompanyAccount, ParentEntity } from "@/contexts/DetailContextProvider.types";

interface DetailModalContentWithEntityContextProps<T extends BaseEntity> {
  itemId: string;
  item: T | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  modalId: string;
  entityName: string;
  config: SimpleTableConfig<T>;
  actionContext: DetailActionContext<T> | null;
  detailActions: any[];
  entityTabsConfig: any;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  headerTitle: string;
  headerSubtitle: string;
  canNavigate: boolean | null;
  canGoPrevious: boolean | null;
  canGoNext: boolean | null;
  handlePrevious: () => void;
  handleNext: () => void;
  handleInlineEdit: (item: T) => Promise<void>;
  handleEdit: (item: T) => void;
  handleDelete: (item: T) => Promise<void>;
  parentEntity: ParentEntity | undefined;
  linkedCompanyAccount?: LinkedCompanyAccount | null;
}

/**
 * DetailModalContent wrapper that adds EntityContextProvider and Ask Meridian components
 * This allows the Ask Meridian button to appear in detail modals
 * 
 * MEMOIZED: Prevents re-renders when parent re-renders with same props
 */
function DetailModalContentWithEntityContextComponent<T extends BaseEntity>({
  itemId,
  item,
  isLoading,
  error,
  refetch,
  modalId,
  entityName,
  config,
  actionContext,
  detailActions,
  entityTabsConfig,
  activeTab,
  setActiveTab,
  headerTitle,
  headerSubtitle,
  canNavigate,
  canGoPrevious,
  canGoNext,
  handlePrevious,
  handleNext,
  handleInlineEdit,
  handleEdit,
  handleDelete,
  parentEntity,
  linkedCompanyAccount = null,
}: DetailModalContentWithEntityContextProps<T>) {
  // Memoize entity context data
  const entityContextData = useMemo(() => {
    if (!item) return null;
    return {
      entityType: entityName,
      entityId: itemId,
      entityData: item,
      entityDisplayName: headerTitle,
    };
  }, [item, entityName, itemId, headerTitle]);

  // If no entity context, render without it
  if (!entityContextData) {
    return (
      <DetailModalContent
        itemId={itemId}
        item={item}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        modalId={modalId}
        entityName={entityName}
        config={config}
        actionContext={actionContext}
        detailActions={detailActions}
        entityTabsConfig={entityTabsConfig}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        headerTitle={headerTitle}
        headerSubtitle={headerSubtitle}
        canNavigate={canNavigate}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        handleInlineEdit={handleInlineEdit}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        parentEntity={parentEntity}
        linkedCompanyAccount={linkedCompanyAccount}
      />
    );
  }

  return (
    <EntityContextProvider
      entityType={entityContextData.entityType}
      entityId={entityContextData.entityId}
      entityData={entityContextData.entityData}
      entityDisplayName={entityContextData.entityDisplayName}
    >
      <DetailModalContent
        itemId={itemId}
        item={item}
        isLoading={isLoading}
        error={error}
        refetch={refetch}
        modalId={modalId}
        entityName={entityName}
        config={config}
        actionContext={actionContext}
        detailActions={detailActions}
        entityTabsConfig={entityTabsConfig}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        headerTitle={headerTitle}
        headerSubtitle={headerSubtitle}
        canNavigate={canNavigate}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        handleInlineEdit={handleInlineEdit}
        handleEdit={handleEdit}
        handleDelete={handleDelete}
        parentEntity={parentEntity}
        linkedCompanyAccount={linkedCompanyAccount}
      />
    </EntityContextProvider>
  );
}

// ============================================================================
// CUSTOM COMPARISON FOR WRAPPER MEMOIZATION
// ============================================================================

/**
 * Only re-render if critical props change.
 * This prevents re-renders when parent re-renders with same data.
 */
function areDetailModalContentWithEntityContextPropsEqual<T extends BaseEntity>(
  prevProps: DetailModalContentWithEntityContextProps<T>,
  nextProps: DetailModalContentWithEntityContextProps<T>
): boolean {
  // Re-render if item ID changes
  if (prevProps.itemId !== nextProps.itemId) {
    return false;
  }

  // Re-render if loading or error state changes
  if (prevProps.isLoading !== nextProps.isLoading || prevProps.error !== nextProps.error) {
    return false;
  }

  // Re-render if modal ID changes
  if (prevProps.modalId !== nextProps.modalId) {
    return false;
  }

  // Re-render if entity name changes
  if (prevProps.entityName !== nextProps.entityName) {
    return false;
  }

  // Re-render if config changes
  if (prevProps.config !== nextProps.config) {
    return false;
  }

  // Re-render if action context changes
  if (prevProps.actionContext !== nextProps.actionContext) {
    return false;
  }

  // Re-render if detail actions change
  if (prevProps.detailActions !== nextProps.detailActions) {
    return false;
  }

  // Re-render if entity tabs config changes
  if (prevProps.entityTabsConfig !== nextProps.entityTabsConfig) {
    return false;
  }

  // Re-render if active tab changes
  if (prevProps.activeTab !== nextProps.activeTab) {
    return false;
  }

  // Re-render if header title or subtitle changes
  if (prevProps.headerTitle !== nextProps.headerTitle || prevProps.headerSubtitle !== nextProps.headerSubtitle) {
    return false;
  }

  // Re-render if navigation state changes
  if (
    prevProps.canNavigate !== nextProps.canNavigate ||
    prevProps.canGoPrevious !== nextProps.canGoPrevious ||
    prevProps.canGoNext !== nextProps.canGoNext
  ) {
    return false;
  }

  // Re-render if parent entity or linked company (deal → CRM company) changes
  if (JSON.stringify(prevProps.parentEntity) !== JSON.stringify(nextProps.parentEntity)) {
    return false;
  }

  if (JSON.stringify(prevProps.linkedCompanyAccount ?? null) !== JSON.stringify(nextProps.linkedCompanyAccount ?? null)) {
    return false;
  }

  // Re-render if callbacks change
  if (
    prevProps.setActiveTab !== nextProps.setActiveTab ||
    prevProps.handlePrevious !== nextProps.handlePrevious ||
    prevProps.handleNext !== nextProps.handleNext ||
    prevProps.handleInlineEdit !== nextProps.handleInlineEdit ||
    prevProps.handleEdit !== nextProps.handleEdit ||
    prevProps.handleDelete !== nextProps.handleDelete ||
    prevProps.refetch !== nextProps.refetch
  ) {
    return false;
  }

  // ✅ CRITICAL: Only re-render if item ID or displayed item data changed
  // Don't check entire item object - only check if it's the same item
  if (prevProps.item?.id !== nextProps.item?.id) {
    return false;
  }

  // Props are equal, don't re-render
  return true;
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

export const DetailModalContentWithEntityContext = React.memo(
  DetailModalContentWithEntityContextComponent,
  areDetailModalContentWithEntityContextPropsEqual
) as typeof DetailModalContentWithEntityContextComponent;
